export const en = {
  landing: {
    signIn: 'Open wallet',
    hero: {
      title: 'A crypto wallet with a card and rouble payouts',
      subtitle:
        'Hold USDT, pay with a virtual VISA card and send money to a bank card in roubles — in one app. Deposits on TRON and BSC are free.',
      primary: 'Create a wallet'
    },
    blocks: {
      deposit: {
        title: 'Deposits are free',
        body: 'Your own address on TRON and BSC. We take nothing for crediting USDT — what was sent is what arrives.'
      },
      card: {
        title: 'Virtual VISA card',
        body: 'It costs 50 USDT: 40 to issue and 10 that land on the card. Top it up from your balance; the top-up fee is 5%.'
      },
      payout: {
        title: 'Payouts to a bank card',
        body: 'The partner rate less 3%, and nothing is withheld on top. You see the final rate and the USDT cost before you confirm. Minimum 5,000 ₽.'
      },
      transfer: {
        title: 'Transfers between users',
        body: 'Send to another Novix user by account number — instantly and at no cost.'
      }
    },
    cta: {
      title: 'A wallet takes a minute to open',
      body: 'An email address is all it needs. Deposit addresses are issued as soon as you register.',
      action: 'Get started'
    },
    how: {
      eyebrow: 'Three steps',
      title: 'How to start',
      steps: {
        register: {
          title: 'Register',
          body: 'An email address is all it takes. TRON and BSC deposit addresses are issued straight away.'
        },
        topUp: {
          title: 'Add USDT',
          body: 'Send USDT to your address. We credit exactly what arrives.'
        },
        spend: {
          title: 'Spend it',
          body: 'Issue a card, send to another user, or pay out to a bank card in roubles.'
        }
      }
    },
    advantages: {
      eyebrow: 'In short',
      title: 'Why Novix',
      items: {
        freeDeposit: {
          title: 'Deposits are free',
          body: 'We take nothing for crediting USDT — no percentage and no flat fee.'
        },
        openRate: {
          title: 'A rate with nothing hidden in it',
          body: 'The rate and the amount debited are on screen before you confirm. There is no separate fee on top.'
        },
        oneApp: {
          title: 'Crypto and a card in one place',
          body: 'No separate exchange and separate card: one balance covers both.'
        },
        freeTransfers: {
          title: 'Free transfers inside Novix',
          body: 'Send to another Novix user by account number, at no cost.'
        },
        noSurprises: {
          title: 'The amount is known in advance',
          body: 'Every operation shows what leaves your balance and what arrives, before you press anything.'
        },
        history: {
          title: 'History to the cent',
          body: 'Every operation is listed with its amount, its fee and its status.'
        }
      }
    },
    safety: {
      title: 'How the protection works',
      intro: 'What we avoid: keeping more than we need, and showing figures we cannot explain.',
      items: {
        password: {
          title: 'The password is not stored',
          body: 'The database holds an Argon2id hash. It cannot be turned back into a password, even with full access to the database.'
        },
        pan: {
          title: 'The card number is not returned',
          body: 'Lists and history carry a mask only. Full details are shown on an explicit request and are never stored anywhere.'
        },
        retention: {
          title: 'Payout details are deleted',
          body: 'The destination card number is encrypted and erased after 90 days — only the mask stays in history.'
        },
        ledger: {
          title: 'Double-entry and reconciliation',
          body: 'Every operation is a balanced entry, and balances are reconciled against the providers on a schedule.'
        }
      }
    },
    faq: {
      eyebrow: 'Questions',
      title: 'Common questions',
      items: {
        networks: {
          q: 'Which networks can I deposit on?',
          a: 'USDT on TRON (TRC20) and BSC (BEP20). Both addresses are issued as soon as you register and never change.'
        },
        depositFee: {
          q: 'What does a deposit cost?',
          a: 'Nothing. We withhold neither a percentage nor a flat fee: whatever reaches the address is credited.'
        },
        withdrawFee: {
          q: 'What does a crypto withdrawal cost?',
          a: '1% plus 1 USDT, with a floor of 1 USDT, and the network fee on top — roughly 3 USDT on TRON and 2 USDT on BSC. The final figure is shown before you confirm.'
        },
        cardPrice: {
          q: 'What does a card cost?',
          a: '50 USDT: 40 to issue and 10 that land on the card. Top it up from your balance; the top-up fee is 5%.'
        },
        rate: {
          q: 'How is the rate calculated for rouble payouts?',
          a: 'We take the payment partner rate and worsen it by 3%. That rate and the USDT cost are shown before you confirm, and nothing else is withheld. The minimum payout is 5,000 ₽.'
        },
        timing: {
          q: 'How quickly does money arrive?',
          a: 'A deposit is credited once the network confirms it. A card payout depends on the recipient bank, so we do not promise a time — the status is visible in your history.'
        },
        signup: {
          q: 'What do I need to register?',
          a: 'An email address and a password.'
        }
      }
    },
    footer: 'Novix — crypto wallet, cards and payouts.'
  },
  common: {
    appName: 'Novix',
    loading: 'Loading…',
    retry: 'Retry',
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    back: 'Back',
    continue: 'Continue',
    save: 'Save',
    copy: 'Copy',
    copied: 'Copied',
    copyFailed: 'Could not copy. Select the text manually.',
    download: 'Download',
    done: 'Done',
    language: 'Language',
    next: 'Next',
    // "All" on the button inside an amount field — moves the whole balance.
    max: 'All',
    backHome: 'Back Home',
    seeAll: 'See all',
    quickAmounts: 'Quick amounts',
    noFee: 'No Fee',
    fee: 'Fee: {{amount}}',
    total: 'Total',
    edit: 'Edit',
    delete: 'Delete',
    comingSoon: 'This section arrives in a later phase.'
  },
  nav: {
    primary: 'Primary navigation',
    home: 'Home',
    history: 'History',
    wallet: 'Wallet',
    cards: 'Cards',
    payouts: 'Payouts',
    profile: 'Profile'
  },
  wallet: {
    yourBalance: 'Your balance',
    hideBalance: 'Hide balance',
    showBalance: 'Show balance',
    balanceHidden: 'Balance hidden',
    transactions: 'Transactions',
    addCard: 'Add new',
    totalBalance: 'Total balance',
    balanceNotAvailable: 'Balance is not available yet',
    balanceFailed: 'Could not load the balance',
    actions: {
      deposit: 'Deposit',
      withdraw: 'Withdraw',
      transfer: 'Transfer',
      pay: 'Pay'
    },
    cryptoTile: 'crypto',
    mainAccount: 'Main account',
    yourCards: 'Your cards',
    cardsEmptyTitle: 'No cards yet',
    cardsEmptyBody: 'Issue a virtual VISA card to pay online.',
    cardsLoadFailed: 'Could not load your cards.',
    cardsLabel: 'Your cards',
    recentActivity: 'Recent activity',
    seeAll: 'See all',
    activityScopeNote:
      'This list shows crypto account and virtual card operations: issue, top-up and refund. Card purchases are not shown here yet.'
  },
  fees: {
    title: 'Fees & Limits',
    linkHint: 'View transaction fees and account limits',
    unavailable: 'Could not load this section. Try again later.',
    zeroPercent: '0% fee',
    from: 'from {{amount}}',
    percent: '{{value}}%',
    percentPlus: '{{value}}% + {{fixed}}',
    percentFee: '{{value}}% fee',
    exampleLabel: 'Example:',
    networkShort: {
      trc20: 'TRC20',
      bsc: 'BEP20'
    },
    crypto: {
      heading: 'Crypto deposits & withdrawals',
      deposit: 'Crypto deposits',
      withdrawal: 'Crypto withdrawals',
      serviceFee: 'Service fee:',
      boundsBoth: 'min. {{min}}, max. {{max}}',
      boundsMin: 'min. {{min}}',
      boundsMax: 'max. {{max}}',
      plusNetworkFee: 'Plus a network fee',
      limits: 'Withdrawal limits',
      minimum: 'Minimum',
      minimumOn: 'Minimum on {{network}}',
      dailyLimit: 'Daily limit',
      example: 'Example: send {{amount}}',
      exampleFee: 'Service fee: {{fee}}. Total debited:'
    },
    cards: {
      heading: 'Cards & RUB withdrawals',
      issuance: 'Card issuance',
      issuanceOnce: 'One-time issuance fee',
      firstDeposit: 'Plus {{amount}} loaded onto the card',
      recharge: 'Card top-up',
      rechargeOnTop: 'Added to the top-up amount',
      rechargeRange: 'Top-up range: {{min}} – {{max}}',
      rechargeExample: '{{amount}} top-up = {{total}} total',
      payments: 'Card payments'
    },
    rub: {
      title: 'Withdraw to a RUB bank card',
      includedInRate: 'Included in the rate',
      adjustment:
        'No separate fee is charged for withdrawals to a bank card in RUB. A {{value}}% adjustment is already included in your exchange rate.',
      noSeparateFee:
        'No separate fee is charged for withdrawals to a bank card in RUB: it is included in your exchange rate.',
      minimum: 'Minimum withdrawal: {{amount}}',
      example: '{{market}} RUB/USDT becomes {{customer}} RUB/USDT after the adjustment.'
    }
  },
  activity: {
    title: 'Activity',
    empty: 'No operations yet.',
    unavailable: 'Could not load the activity history.',
    partial: 'Part of the history could not be loaded — this list may be incomplete.',
    toUser: 'To user #{{id}}',
    fromUser: 'From user #{{id}}',
    cardUnknown: 'Card',
    virtualCard: 'Virtual card',
    virtualCardMasked: 'Card {{mask}}',
    openDetail: 'Open operation details',
    kind: {
      deposit: { in: 'Deposit', out: 'Deposit' },
      withdrawal: { in: 'Crypto withdrawal', out: 'Crypto withdrawal' },
      payout: { in: 'Payout to card', out: 'Payout to card' },
      transfer: { in: 'Incoming transfer', out: 'Outgoing transfer' },
      cardIssue: { in: 'Card issue', out: 'Card issue' },
      cardTopUp: { in: 'Card top-up', out: 'Card top-up' },
      cardRefund: { in: 'Card refund', out: 'Card refund' }
    },
    status: {
      pending: 'In progress',
      completed: 'Completed',
      failed: 'Declined',
      // Deliberately a different word and a different colour from "Declined":
      // the operation did not go through, and the money is back. Two facts.
      reversed: 'Refunded',
      unresolved: 'Being confirmed'
    },
    secondary: {
      deposit: 'Credited {{amount}}',
      withdrawal: 'Debited {{amount}}',
      payout: 'Debited {{amount}}',
      transfer: 'Debited {{amount}}',
      cardIssue: 'Debited {{amount}}',
      cardTopUp: 'Debited {{amount}}',
      cardRefund: 'Credited {{amount}}'
    },

    // History filters. "Account" is which of the customer's two accounts is
    // touched: the crypto account or a virtual card.
    filters: {
      label: 'History filters',
      search: 'Search operations',
      searchPlaceholder: 'ID, hash, address, card or amount',
      account: 'Account',
      accountAll: 'All accounts',
      accountCrypto: 'Crypto account',
      accountCard: 'Virtual card',
      kind: 'Operation type',
      kindAll: 'All types',
      from: 'From',
      to: 'To',
      reset: 'Reset filters',
      show: 'Filters',
      hide: 'Hide filters',
      activeCount: 'Filters · {{count}}',
      nothingFound: 'No operations match these filters.',
      found: 'Operations found: {{count}}',
      kinds: {
        deposit: 'Deposit',
        withdrawal: 'Crypto withdrawal',
        payout: 'Payout to card',
        transfer: 'Transfer',
        cardIssue: 'Card issue',
        cardTopUp: 'Card top-up',
        cardRefund: 'Card refund'
      }
    },

    // The single-operation screen. Most of its labels are reused from
    // withdraw.* and deposit.*, where the same words are already translated —
    // a second copy would drift from the first. Only what is missing lives here.
    detail: {
      summaryLabel: 'Operation details',
      detailsLabel: 'Operation',
      // Past tense: this already happened. withdraw.crypto's wording is future,
      // because that one is a confirmation screen.
      totalDebit: 'Debited from your balance',
      usdtDebit: 'Debited in USDT',
      recipient: 'Recipient',
      sender: 'Sender',
      accountNumber: '#{{id}}',
      noteLabel: 'Note',
      failureTitle: 'This operation did not go through',
      openInExplorer: 'Open in {{explorer}}',
      backToHistory: 'Go to activity history',
      notFoundTitle: 'Operation not found',
      notFoundBody:
        'This operation is not in your history. The link may be out of date, or belong to another account.',
      // Kept apart from "not found": a failed request is no evidence that the
      // operation does not exist.
      unavailableTitle: 'Could not load this operation',
      unavailableBody:
        'The activity history is unavailable right now, so this operation cannot be shown. Check your connection and try again later.'
    },

    share: {
      button: 'Share',
      title: 'Novix operation receipt',
      downloaded: 'The receipt has been saved to a file.',
      failed: 'Could not prepare the receipt.'
    }
  },
  withdraw: {
    methodTitle: 'Withdraw',
    methodSubtitle: 'Choose where to send your funds.',
    methodCrypto: 'Cryptocurrency',
    methodCryptoHint: 'USDT to an external address on TRON or BNB Smart Chain',
    methodCard: 'Bank card',
    methodCardHint: 'Roubles to a card, from ₽5,000',
    methodTransfer: 'Transfer inside Novix',
    methodTransferHint: 'USDT to another user by their ID',

    amountLabel: 'Amount in {{currency}}',
    amountNotANumber: 'Enter the amount as a number',
    max: 'Max',
    confirm: 'Confirm withdrawal',
    quoteUnavailableTitle: 'Quote unavailable',
    historyPollNote: 'The status updates automatically.',

    // The review step. Sending money out is irreversible; this is where a
    // pasted-from-the-wrong-tab address and an extra zero get caught.
    review: {
      summaryLabel: 'Review this operation',
      edit: 'Edit details',
      note: 'Check the amount and the recipient. This cannot be undone once confirmed.'
    },
    receipt: {
      summaryLabel: 'Operation details',
      dateTime: 'Date and time',
      txHash: 'Transaction hash',
      operationId: 'Operation ID'
    },

    destination: {
      crypto: 'to the address',
      card: 'to the card'
    },

    submitted: {
      // A 200 is not "sent". The funds are debited and queued; the word "sent"
      // only appears once the backend has confirmed completion.
      pending: {
        title: 'Request accepted',
        body: 'The funds have been debited and queued for sending {{destination}}. Nothing has been sent yet — the status updates automatically.'
      },
      completed: {
        title: 'Sent',
        body: 'The funds have been sent {{destination}}.'
      },
      reversed: {
        title: 'Cancelled and refunded',
        body: 'Sending {{destination}} did not succeed. The full amount has been returned to your balance.'
      },
      unresolved: {
        title: 'Outcome is being confirmed',
        body: 'We do not yet know whether the funds reached {{destination}}. A specialist is checking; nothing is refunded automatically in this state.'
      },
      failed: {
        title: 'Operation declined',
        body: 'Sending {{destination}} did not succeed.'
      },
      reason: 'Reason: {{reason}}',
      startAnother: 'Start another operation'
    },

    crypto: {
      title: 'Crypto withdrawal',
      subtitle: 'Choose the network, the destination address and the amount.',
      chooseNetwork: 'Withdrawal network',
      addressLabel: 'Destination address',
      addressHint: 'An address on {{network}}.',
      addressPlaceholder: {
        trc20: 'T…',
        bsc: '0x…'
      },
      addressInvalid: 'This does not look like a {{network}} address. Please check it.',
      addressOtherNetwork:
        'This looks like an address on another network, not {{network}}. Sending to it would lose the funds permanently.',
      warningTitle: 'Sending on {{network}} only',
      warningBody:
        'The funds will be sent over {{network}}. If the destination address belongs to a different network, the {{currency}} will be lost permanently — the transfer cannot be reversed.',
      recipientGets: 'Recipient gets',
      networkFee: 'Network fee',
      networkFeeHint: 'Charged on top of the amount',
      commission: 'Novix commission',
      totalDebit: 'Debited from your balance',
      minimum: 'Minimum amount',
      maxWithdrawable: 'Available to withdraw',
      maxWithdrawableHint: 'Balance less the fees',
      remainingDailyLimit: 'Remaining daily limit',
      summaryLabel: 'Withdrawal breakdown',
      networkRow: 'Network',
      recipientRow: 'Recipient',
      belowMinimum: 'The minimum withdrawal is {{amount}}',
      aboveMax: 'You can withdraw at most {{amount}}',
      historyTitle: 'Crypto withdrawals'
    },

    card: {
      title: 'Payout to a card',
      subtitle: 'Enter the amount in roubles and the recipient card.',
      amountLabel: 'Amount in roubles',
      minimumHint: 'The minimum payout is {{amount}}.',
      belowMinimum: 'The minimum payout is {{amount}}',
      belowMinimumUnknown: 'That amount is below the minimum for a card payout.',
      recipientGets: 'The card receives',
      rate: 'Rate',
      rateValue: '1 USDT = ₽{{rate}}',
      usdtDebit: 'Debited from your balance',
      usdtDebitHint: 'At the rate shown',
      minimum: 'Minimum amount',
      summaryLabel: 'Payout breakdown',
      cardRow: 'Recipient card',
      amountRow: 'Amount',
      numberLabel: 'Recipient card number',
      numberHint: '16 digits from the front of the card.',
      numberInvalid: 'Please check the card number — there is a mistake in it.',
      numberChecked: 'Card number passes the check',
      numberRejected: 'The bank did not accept this card number. Please check it and try again.',
      privacyNote: 'The card number goes to the payment partner only and is never stored here.',
      historyTitle: 'Card payouts'
    }
  },
  transfer: {
    title: 'Transfer inside Novix',
    subtitle: 'Send USDT to another Novix user by their ID.',
    recipientLabel: 'Recipient ID',
    recipientHint: 'The numeric identifier of a Novix user.',
    recipientInvalid: 'The recipient ID is a number.',
    noteLabel: 'Note (optional)',
    noteHint: 'The recipient will see it. Up to 140 characters.',
    recipientGets: 'Recipient gets',
    fee: 'Novix commission',
    feeHint: 'Charged on top of the amount',
    totalDebit: 'Debited from your balance',
    availableBalance: 'Available balance',
    summaryLabel: 'Transfer breakdown',
    recipientRow: 'Recipient',
    reviewNote: 'The recipient will see this transfer in the Novix app.',
    insufficientFunds: 'Your balance does not cover this transfer and its commission.',
    belowMinimum: 'The minimum transfer is {{amount}}',
    confirm: 'Send transfer',
    // A transfer is the one money operation with no external call: a single
    // ledger transaction, so "completed" here is the truth.
    sentTitle: 'Transfer completed',
    sentBody: 'User #{{id}} received {{amount}}.',
    sendAnother: 'Make another transfer',
    historyTitle: 'Transfers'
  },
  deposit: {
    methodTitle: 'Deposit',
    methodSubtitle: 'Choose how you want to top up your account.',
    methodCrypto: 'Cryptocurrency',
    methodCryptoHint: 'USDT on TRON and BNB Smart Chain',
    methodCard: 'Bank card',
    methodInternal: 'Transfer from another user',

    shareId: {
      rowHint: 'Show your ID for transfers',
      transferLink: 'My ID for receiving a transfer',
      heading: 'Your Novix ID',
      lead: 'Send this ID to whoever wants to transfer funds to you — it is how they find your account.',
      scopeNote:
        'This ID works only for transfers between Novix users. It cannot be used to top up from another wallet or a bank.',
      failedTitle: 'Could not load your ID'
    },

    cryptoTitle: 'Crypto deposit',
    cryptoSubtitle: 'Pick a network and send funds to the address below.',
    chooseNetwork: 'Choose a network',
    networks: {
      trc20: 'TRON (TRC20)',
      bsc: 'BNB Smart Chain (BEP-20)',
      unknown: 'Unknown network'
    },
    networkHints: {
      trc20: 'Tron network',
      bsc: 'BNB Smart Chain network'
    },

    addressFor: 'Your {{currency}} address on {{network}}',
    addressLabel: 'Deposit address',
    addressLength: 'Address length is {{length}}. It is shown in full, never shortened.',
    copyAddress: 'Copy address',
    qrLabel: 'QR code of the {{currency}} address on {{network}}',

    warning: {
      title: 'Send on {{network}} only',
      body: 'A transfer sent on any other network is lost permanently and cannot be recovered. Make sure the sending wallet is sending {{currency}} on {{network}}.'
    },

    minimumLabel: 'Minimum deposit',
    minimumHint: 'A transfer smaller than {{amount}} will not be credited.',
    feeFrom: 'from {{amount}}',
    // The method chooser's pill: chosen when the API states a fee of zero, never
    // as a fallback for a fee that could not be read. See DepositMethodPage.
    //
    // Its own key rather than common.noFee (which the address screen's fee row
    // uses) because the pill has a hard width budget: at 390px it sits against
    // "Криптовалюта", which cannot hyphenate, and the longer wording breaks that
    // word in half. Already fixed once — see the comment on FeePill.
    feeNone: 'Free',
    feeLabel: 'Provider fee',
    feeHint:
      'The provider deducts {{fee}} from every {{network}} deposit, whatever the amount. Sending a small amount means most of it goes to the fee.',
    feeFreeHint: 'A {{network}} deposit is credited in full — the provider takes no commission.',
    // The history row's own figure, from the webhook. It exists because a row
    // can carry a fee on a day the screen above says deposits are free.
    rowFee: 'Fee {{amount}}',

    addressFailedTitle: 'Could not get the address',

    historyTitle: 'Deposits',
    historyEmpty: 'No deposits yet.',
    historyUnavailable: 'Could not load the deposit history.',
    historyPollNote: 'The list refreshes automatically. Crediting takes a few minutes.',
    rowTitle: 'Deposit',
    partnerCredit: 'Partner credit',
    credited: 'Credited {{amount}}',
    openReceipt: 'Open deposit details',
    receipt: {
      summaryLabel: 'Deposit details',
      detailsLabel: 'Transaction',
      amount: 'Amount sent',
      credited: 'Credited to your balance',
      creditedPending: 'Known once it is credited',
      batchReference: 'Partner batch',
      dateTime: 'Date and time',
      txHash: 'Transaction hash',
      operationId: 'Operation ID',
      notFoundTitle: 'Deposit not found',
      notFoundBody: 'This operation is not in your deposit history.'
    },
    status: {
      pending: 'Processing',
      confirmed: 'Credited',
      failed: 'Rejected'
    },
    backHome: 'Back to home'
  },
  auth: {
    login: 'Sign in',
    email: 'Email',
    emailPlaceholder: 'you@example.com',
    password: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    signIn: 'Sign in',
    signOut: 'Sign out',

    welcomeTitle: 'Novix — your USDT wallet',
    loginTitle: 'Sign in to your account',
    loginSubtitle: 'Enter your email and password to continue',
    forgotPassword: 'Forgot your password?',
    noAccount: 'No account yet?',
    createAccount: 'Create an account',
    haveAccount: 'Already have an account?',
    backToLogin: 'Back to sign in',

    gate: {
      checking: 'Checking your session…',
      failedTitle: 'Could not check your session',
      // Deliberately does not claim the user is signed out: we do not know.
      failedBody:
        'We cannot reach the server, so we cannot confirm you are signed in. Check your connection and try again — or sign in afresh.'
    },

    sessionEnded: {
      revokedTitle: 'You were signed out for security',
      revoked:
        'The session was terminated: an already-rotated refresh token was replayed. If this was not you, change your password right after signing in.',
      expiredTitle: 'Session ended',
      expired: 'Your session expired. Please sign in again.'
    },

    register: {
      title: 'Create an account',
      step: 'Step {{current}} of {{total}}',
      emailStepTitle: 'Your email',
      emailStepHint: 'We send the confirmation link to this address.',
      passwordStepTitle: 'Choose a password',
      passwordStepHint: 'You will use it to sign in to {{email}}.',
      confirmPassword: 'Repeat password',
      submit: 'Create account',
      changeEmail: 'Change email',
      successTitle: 'Check your inbox',
      // Wording deliberately does not confirm whether the account exists — the
      // backend answers identically for a new and an already-taken address.
      successBody:
        'If this address can be registered, we have sent a confirmation link to {{email}}.',
      successHint: 'The link is valid for a limited time. Check your spam folder.',
      resend: 'Send the email again',
      resendIn: 'You can resend in {{seconds}}s',
      resent: 'Email sent again.',
      devLinkTitle: 'Development mode',
      devLinkBody: 'The backend returned a confirmation token. This never happens in production.',
      devLinkAction: 'Open the confirmation link'
    },

    passwordRules: {
      requirementsTitle: 'Your password must contain:',
      minLength: 'at least {{count}} characters',
      letterAndDigit: 'at least one letter and one digit',
      maxLength: 'at most {{count}} characters',
      required: 'Enter a password',
      tooShort: 'The password must be at least {{count}} characters long',
      tooLong: 'The password must be at most {{count}} characters long',
      needsLetterAndDigit: 'The password must contain at least one letter and one digit',
      mismatch: 'The passwords do not match',
      requirementMet: 'met',
      requirementUnmet: 'not met'
    },

    emailErrors: {
      required: 'Enter your email address',
      invalid: 'Check your email address',
      tooLong: 'The email address is too long'
    },

    confirmEmail: {
      title: 'Email confirmation',
      verifying: 'Checking the link…',
      successTitle: 'Email confirmed',
      successBody: 'Your account is active. You can sign in now.',
      failedTitle: 'This link is not valid',
      failedBody: 'The link has already been used or has expired. Request a new one.',
      missingTokenTitle: 'No confirmation token',
      missingTokenBody:
        'Open the full link from the email — the address is missing the token parameter.',
      goToLogin: 'Go to sign in',
      requestNewLink: 'Request a new link'
    },

    forgot: {
      title: 'Password recovery',
      subtitle: 'Enter your account email and we will send a password reset link.',
      submit: 'Send the link',
      sentTitle: 'Check your inbox',
      // Same principle as registration: the response never reveals whether the
      // account exists.
      sentBody: 'If an account exists for {{email}}, we have sent a password reset link to it.',
      sentHint: 'The link is valid for a limited time. Check your spam folder.'
    },

    reset: {
      title: 'New password',
      subtitle: 'Choose a new password for your account.',
      submit: 'Save password',
      successTitle: 'Password changed',
      successBody: 'Sign in with your new password.',
      missingTokenTitle: 'No reset token',
      missingTokenBody:
        'Open the full link from the email — the address is missing the token parameter.',
      requestNewLink: 'Request a new link'
    }
  },
  otp: {
    label: 'Verification code',
    digit: 'Digit {{index}} of {{total}}',
    resendIn: 'Resend in {{seconds}}s',
    resendPrompt: 'No code?',
    resend: 'Send again',
    resending: 'Sending…'
  },
  // The transfer contact book. A contact is a private nickname over an id: the
  // server deliberately will not say whether a user id exists, so nothing below
  // promises a check that never happened.
  contacts: {
    title: 'Contacts',
    subtitle: 'Your own names for the ids you send to.',
    add: 'Add contact',
    idLine: 'ID {{id}}',
    editAction: 'Rename contact “{{label}}”',
    deleteAction: 'Delete contact “{{label}}”',
    emptyTitle: 'No contacts yet',
    emptyBody:
      'A contact is your own name for a Novix user id. Save an id once and you will not have to type it again when you send.',
    emptyPrivacy:
      'Only you can see your contacts. Novix does not check the id and does not tell anyone you saved them.',
    loadFailedTitle: 'Could not load your contacts',
    goToTransfer: 'Go to transfer',
    added: 'Contact “{{label}}” added',
    renamed: 'Contact renamed to “{{label}}”',
    deleted: 'Contact “{{label}}” deleted',
    deleteTitle: 'Delete this contact?',
    deleteBody: 'The contact “{{label}}” will be removed from your book. This cannot be undone.',
    deleteKeeps:
      'Past transfers stay in your history — only the entry in your contact book is removed.',
    pickerTitle: 'Choose a contact',
    pickerHint: 'The transfer goes to the id saved in this contact.',
    manage: 'Manage contacts',
    chooseFromBook: 'Choose from contacts',
    clearChoice: 'Clear',
    form: {
      addTitle: 'New contact',
      addHint:
        'Novix does not check whether this user exists and does not notify them. Please check the id yourself.',
      renameTitle: 'Rename contact',
      renameHint: 'Only the name changes. The recipient id stays the same.',
      idLabel: 'Novix user id',
      idHint: 'The numeric id the recipient sees on their own profile.',
      idInvalid: 'An id is a number.',
      idFixed: 'ID {{id}} — cannot be changed',
      labelLabel: 'What to call them',
      labelHint: 'Only you see this. Up to {{max}} characters.',
      labelTooLong: 'That name is longer than {{max}} characters.',
      addSubmit: 'Add'
    }
  },

  // The saved-card book for rouble payouts. No endpoint returns a full card
  // number — every screen shows the server's mask and nothing else.
  cards: {
    title: 'Saved cards',
    subtitle: 'Cards for rouble payouts, without retyping the number.',
    add: 'Add card',
    emptyTitle: 'No saved cards yet',
    emptyBody:
      'Save a card once and you can pick it from a list when you withdraw roubles, instead of typing the number.',
    emptyPrivacy:
      'The number is stored encrypted and is never shown in full — not here and not in your history.',
    loadFailedTitle: 'Could not load your cards',
    goToPayout: 'Go to card withdrawal',
    added: 'Card {{mask}} saved',
    edited: 'Card {{mask}} updated',
    deleted: 'Card {{mask}} deleted',
    deleteTitle: 'Delete this card?',
    deleteBody: 'Card {{mask}} will be removed from your saved cards. This cannot be undone.',
    deleteKeeps:
      'Past payouts stay in your history — only the saved card is removed, not the payments made to it.',
    pickerTitle: 'Choose a card',
    pickerHint: 'The money goes to the card you choose.',
    manage: 'Manage cards',
    editAction: 'Edit card {{mask}}',
    deleteAction: 'Delete card {{mask}}',
    chooseFromBook: 'Choose a saved card',
    clearChoice: 'Clear',
    unreadableInstruction:
      'This card can no longer be read. Delete it and save it again — it cannot be paid to right now.',
    form: {
      addTitle: 'New card',
      addHint: 'The card number is sent once and never comes back.',
      editTitle: 'Edit card',
      editHint: 'The card number cannot be changed. To use a different one, save a new card.',
      numberLabel: 'Card number',
      labelLabel: 'Card name',
      labelPlaceholder: 'For example, “Salary”',
      labelHint: 'Only you see this. Up to {{max}} characters.',
      labelRequired: 'Give the card a name — it is how you recognise it in the list.',
      labelTooLong: 'That name is longer than {{max}} characters.',
      addSubmit: 'Save card',
      privacyNote:
        'We store the number encrypted and show only the first six and last four digits.',
      rateLimited: 'Too many attempts. Wait a moment and try saving the card again.'
    }
  },

  /**
   * The card product: issuing, topping up, card details, OTP, closing.
   *
   * Separate from `cards`, which is the saved-card book for rouble payouts. They
   * are different products and sharing a namespace would only invite confusion.
   */
  cardProduct: {
    title: 'Virtual cards',
    subtitle: 'A virtual VISA card for paying online.',
    loadFailed: 'Your cards could not be loaded.',
    emptyTitle: 'No cards yet',
    emptyBody: 'Issue a virtual VISA card — you can pay online with it as soon as it is issued.',
    add: 'Issue a card',
    openCard: 'Open card',
    listLabel: 'Your cards',
    maskedUnknown: 'The number appears once the card is issued',
    balanceLabel: 'Balance',
    balanceUnknown: 'Balance is unavailable right now',
    issuedAt: 'Issued {{date}}',
    requestedAt: 'Requested {{date}}',
    notFound: 'No such card.',
    back: 'Back to cards',
    actions: 'Card actions',
    failureReason: 'Reason: {{reason}}',

    // Card terms. The figures come from GET /cards/pricing — only the labels
    // live here. "0 %" and "24/7" are product facts, not tariff rows.
    terms: {
      title: 'Terms',
      rechargeFee: 'Card top-up fee',
      support: 'Support',
      supportValue: '24/7',
      transactionFee: 'Transaction fee',
      transactionFeeValue: '0 %',
      issuanceFee: 'Card issue price',
      rechargeLimits: 'Limits per top-up',
      rechargeLimitsValue: '{{min}} – {{max}}',
      percent: '{{value}} %',
      unavailable: 'Terms are unavailable right now.'
    },

    status: {
      issuing: 'Issuing',
      active: 'Active',
      closing: 'Closing',
      closed: 'Closed',
      failed: 'Not issued',
      unresolved: 'Being confirmed'
    },

    accepted: {
      issuing:
        'Your request has been accepted and the amount debited. The card appears here once the partner issues it.',
      active: 'The card is issued and ready to pay with.',
      closing:
        'Your closure request has been accepted. Whatever is left returns to your crypto account once the closure completes.',
      closed: 'The card is closed.',
      failed:
        'The card could not be issued. What you were charged has been returned to your balance.',
      unresolved:
        'The partner has not confirmed this issuance yet. We will update it as soon as they do — there is no need to request another card.'
    },

    issue: {
      title: 'Issue a card',
      intro: 'A virtual VISA card for paying online.',
      feeLabel: 'Issuance fee',
      depositLabel: 'Credited to the card',
      totalLabel: 'Total charge',
      topUpFeeNote: 'Topping the card up costs {{percent}} % of the amount.',
      review: 'Check the terms',
      buy: 'Issue a card',
      confirm: 'Confirm issuance',
      edit: 'Back',
      priceUnavailableTitle: 'The price is unavailable right now',
      priceUnavailableBody:
        'We cannot show the current price, so issuing is unavailable for the moment. Please try again later.',
      acceptedTitle: 'Request accepted',
      chargedLabel: 'Charged'
    },

    topUp: {
      title: 'Top up card',
      amountLabel: 'Amount in USDT',
      available: 'Available: {{amount}}',
      presetsLabel: 'Quick amounts',
      notEnough: 'Not enough funds',
      belowMinimum: 'The minimum is {{amount}}',
      aboveMaximum: 'The maximum is {{amount}}',
      summaryAmount: 'Amount',
      summaryFee: 'Fee',
      summaryTotal: 'Total charge',
      estimateNote: 'The final fee is calculated by the server when you confirm.',
      review: 'Check the top-up',
      submit: 'Top up',
      confirm: 'Confirm top-up',
      edit: 'Edit',
      unavailableTitle: 'Top-ups are unavailable right now',
      unavailableBody: 'We cannot work out the fee. Please try again later.',
      acceptedTitle: 'Top-up accepted',
      acceptedBody:
        'The amount has been debited and sent to the card. The card balance updates once it arrives.',
      noTracking: 'A top-up has no separate status — watch the card balance on the card screen.',
      cardCredited: 'Credited to the card',
      rate: 'Rate',
      status: {
        pending: 'Sent',
        completed: 'Credited',
        reversed: 'Returned',
        unresolved: 'Being confirmed'
      }
    },

    info: {
      title: 'Card details',
      reveal: 'Show details',
      hide: 'Hide details',
      hint: 'These details are shown once and are not stored anywhere.',
      autoHide: 'The details hide themselves after a minute and a half.',
      hidden: 'The details are hidden.',
      number: 'Card number',
      expiry: 'Expiry',
      cvv: 'CVV',
      holder: 'Cardholder',
      country: 'Country',
      city: 'City',
      address: 'Address',
      postcode: 'Post code',
      billingLabel: 'Billing address',
      verifyOtp: 'Verify OTP code'
    },

    otp: {
      title: 'One-time code',
      intro:
        'A one-time code confirms an online purchase. It only exists once a shop has asked for one.',
      request: 'Get a code',
      again: 'Request again',
      preparing: 'Preparing your code…',
      codeLabel: 'One-time code',
      expiresIn: 'Valid for another {{time}}',
      announce: 'The code is valid for another {{time}}.',
      expiredTitle: 'The code has expired',
      expiredBody: 'That code no longer works. Request a new one.',
      noneTitle: "There's no active code right now",
      noneBody:
        'That is normal: a code appears at the moment of payment. Start the payment in the shop, then request a code again.'
    },

    close: {
      title: 'Close card',
      intro: 'Closing is permanent: the card can no longer be used.',
      refundNote:
        'Whatever is left returns to your crypto account once the closure completes. The issuing bank decides the amount — it may be less than the balance, or nothing at all.',
      submit: 'Close card',
      confirmTitle: 'Close this card?',
      confirmBody: 'This cannot be undone. The card can no longer be used.',
      confirmAction: 'Yes, close it',
      closingTitle: 'The card is closing',
      closedTitle: 'Card closed',
      refundPending: 'The refunded amount will be known once the closure completes.',
      refundLabel: 'Returned to your crypto account',
      refundZero: 'Nothing was returned: there was no balance left on the card.'
    }
  },

  profile: {
    title: 'Profile',
    idLabel: 'ID {{id}}',
    copyId: 'Copy ID',
    copyIdValue: 'Copy ID {{id}}',
    idUnavailable: 'ID is not available yet',
    contactSupport: 'Contact\nThe Support',
    contactSupportPlain: 'Contact the support',
    supportUnavailable: 'The support channel is not connected yet.',
    sectionProfile: 'Profile',
    sectionSettings: 'Settings',
    accountSafety: 'Account safety',
    contacts: 'Contacts',
    contactsHint: 'Your names for the ids you send to',
    savedCards: 'Saved cards',
    savedCardsHint: 'Cards for rouble payouts',
    emailRow: 'Email',
    emailUnknown: 'Not set',
    passwordRow: 'Password',
    signOut: 'Sign Out',
    signOutConfirmTitle: 'Sign Out',
    signOutConfirmBody:
      'Are you sure you want to sign out? You’ll need to enter your credentials again the next time you sign in.',
    stayLoggedIn: 'Stay Logged In',
    signedOut: 'You have signed out.'
  },
  theme: {
    label: 'Theme',
    light: 'Light',
    dark: 'Dark',
    system: 'System',
    toggle: 'Toggle theme'
  },
  errors: {
    title: 'Something went wrong',
    description: 'This section could not be displayed. Try reloading the page.',
    notFound: 'Page not found',
    notFoundDescription: 'This page does not exist or has been moved.',
    goHome: 'Go home',
    unknown: 'Unexpected error. Please try again.',
    kind: {
      http: 'The service is temporarily unavailable. Please try again later.',
      network: 'Cannot reach the server. Check your connection.',
      timeout: 'The server did not respond in time. Try again.',
      'rate-limit': 'Too many requests. Please wait {{seconds}}s.',
      unauthorized: 'Your session expired. Please sign in again.',
      parse: 'The server returned an unexpected response.'
    },
    code: {
      // Keys are the backend envelope's errorCode, verbatim. Parsing the message
      // text is forbidden: it is unstable and untranslated.
      invalid_credentials: 'Incorrect email or password.',
      account_locked: 'Too many attempts. Wait 15 minutes and try again.',
      account_blocked: 'This account is blocked. Contact support.',
      invalid_token: 'The link is invalid or has expired.',
      invalid_refresh_token: 'Your session expired. Please sign in again.',
      refresh_token_reuse: 'The session was terminated for security. Please sign in again.',
      unauthenticated: 'You need to sign in.',
      validation_failed: 'Check the fields and try again.',
      INSUFFICIENT_FUNDS: 'Insufficient funds.',

      // Crypto withdrawal
      crypto_unsupported_network: 'That network is not supported.',
      crypto_invalid_address: 'That address does not match the selected network. Please check it.',
      crypto_address_not_activated:
        'That TRON address has never been used and cannot receive USDT yet. Send it a small amount of TRX first.',
      crypto_withdrawal_below_minimum: 'That amount is below the minimum for this network.',
      crypto_withdrawal_insufficient_funds:
        'Your balance does not cover this amount together with the network fee.',
      crypto_withdrawal_daily_limit_exceeded:
        'This would exceed your daily withdrawal limit. Please try again tomorrow.',
      crypto_withdrawal_temporarily_unavailable:
        'Withdrawals are temporarily unavailable. Please try again shortly.',
      crypto_provider_unavailable:
        'The service is temporarily unavailable. Please try again shortly.',
      crypto_idempotency_key_reused: 'That operation has already been submitted.',

      // Rouble payout to a card
      payout_below_minimum: 'That amount is below the minimum for a card payout.',
      payout_invalid_card_number: 'Please check the card number.',
      payout_insufficient_funds: 'Your balance does not cover this payout.',
      payout_provider_limit: 'That amount is not available right now. Please try a different one.',
      payout_quote_unavailable: 'Payouts are temporarily unavailable. Please try again shortly.',
      payout_idempotency_key_reused: 'That operation has already been submitted.',

      // Transfers
      transfer_below_minimum: 'That amount is below the minimum transfer.',
      transfer_self: 'You cannot transfer to yourself.',
      transfer_recipient_unavailable: 'That recipient cannot receive transfers.',
      transfer_insufficient_funds: 'Your balance does not cover this transfer.',
      transfer_idempotency_key_reused: 'That transfer has already been submitted.',

      // The contact book. All three are safe to show verbatim: they are answers
      // about the caller's own book, not about anybody else's account.
      transfer_contact_not_found: 'That contact is not in your book.',
      transfer_contact_label_invalid: 'Give the contact a name, up to 64 characters.',
      transfer_contact_self: 'You cannot add yourself as a contact.',
      transfer_contact_already_exists: 'That user is already in your contacts.',
      // A client bug rather than a user error: both addressing fields were sent.
      transfer_recipient_ambiguous: 'Give either a recipient id or a contact, not both.',
      transfer_recipient_required: 'Choose who to pay.',

      // Saved cards
      payout_saved_card_not_found: 'No such saved card.',
      payout_saved_card_already_exists: 'That card is already saved — it is in your list.',
      payout_saved_card_unreadable:
        'That saved card can no longer be read. Please remove it and save it again.',
      payout_invalid_card_label: 'A card needs a name of at most 64 characters.',
      payout_card_ambiguous: 'Give either a card number or a saved card, not both.',
      payout_card_required: 'Choose a card to pay to.',

      // The card product. Keys are the codes from
      // src/modules/Novix.Modules.Cards/Infrastructure/CardErrors.cs, one for one.
      card_unauthenticated: 'Please sign in.',
      card_not_found: 'No such card.',
      card_not_active: 'That card cannot be used for this right now.',
      card_already_closing: 'That card is already being closed.',
      card_insufficient_funds: 'Your USDT balance does not cover this.',
      card_idempotency_key_required: 'The request could not be sent. Please try again.',
      card_idempotency_key_reused: 'That request has already been sent.',
      card_amount_out_of_range: 'That amount is outside the limits for a card top-up.',
      card_settings_unavailable: 'Cards are temporarily unavailable. Please try again shortly.',
      card_pricing_inverted: 'Card issuance is temporarily unavailable. Please try again shortly.',
      card_not_yet_issued: 'That card is still being issued. Please try again shortly.',
      card_sensitive_rate_limited:
        'Too many requests for card details. Please wait a moment and try again.',
      card_provider_unavailable:
        'Card details are temporarily unavailable. Please try again shortly.'
    }
  }
} as const;
